import { type Common } from './common.js';
import {
  type AssistantBatchQuery,
  type AssistantBatchQueryCsvRow,
  type AssistantBatchQueryStatus,
  type AssistantReqAuditParams,
  type Modality,
  type ProviderFilters,
  type SourceComponent,
} from './models/assistant/index.js';
import type { FeedbackType } from './models/feedback.js';
import type { ConfigurationWithSession } from './models/organization/assistant/configuration.js';
import type { BasePatientWithEhrInformationId } from './models/patient.js';
import type { PrevisitFormSetWithDetails } from './models/previsit/formSet.js';
import type { ChatMessageType } from './models/report/feedback.js';
import type { UserWithProfileAndPreference } from './models/user.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/patient-previsit/assistant';
  }

  async addMessageToConversationToStream(args: {
    message: ChatMessageType;
    configurationId: string;
    sourceComponent: SourceComponent;
    sessionId?: string;
    isEmbedded?: boolean;
  }) {
    const url = this.common.constructPath(`${this.baseEndpoint}/conversation`);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: true,
      cache: 'no-cache',
      body: JSON.stringify({ ...args, includeCredentials: undefined }),
    });
    if (!response?.body) {
      throw new Error('No response body');
    }
    return response.body.getReader();
  }

  async createMessageFeedback(args: {
    messageId: string;
    feedback: FeedbackType;
    comment: string;
    configurationId: string;
  }) {
    const { messageId, ...body } = args;
    const url = this.common.constructPath(
      `${this.baseEndpoint}/conversation/messages/${messageId}/feedbacks`
    );
    return await this.common.postJSON(url, body, 'POST');
  }

  async findAllBatchQueries(args: {
    assistantConfigurationId: string;
  }): Promise<AssistantBatchQuery[]> {
    const { assistantConfigurationId } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/batch`, {
      assistantConfigurationId,
    });
    return await this.common.getJSON(url);
  }

  async findLatestBatchQueryStatusByAssistantBatchQueryId(
    assistantBatchQueryId: string
  ): Promise<AssistantBatchQueryStatus> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/batch/${assistantBatchQueryId}/status/latest`
    );
    return await this.common.getJSON(url);
  }

  async submitQueriesForBatchProcessing(args: {
    assistantConfigurationId: string;
    csv: AssistantBatchQueryCsvRow[];
    uploadedFileName: string;
  }) {
    const { assistantConfigurationId, csv, uploadedFileName } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/${assistantConfigurationId}/batch`);
    return await this.common.postJSON(url, { csv, uploadedFileName }, 'POST');
  }

  async getResultsCsvSignedUrl(assistantBatchQueryId: string): Promise<{ url: string }> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/batch/${assistantBatchQueryId}/file`
    );
    return await this.common.getJSON(url);
  }

  async getConfigurationById(
    id: string,
    modality?: Modality,
    isEmbedded?: boolean
  ): Promise<ConfigurationWithSession> {
    const url = this.common.constructPath(`${this.baseEndpoint}/configurations/${id}`, {
      isEmbedded,
      modality,
    });
    return await this.common.getJSON(url);
  }

  createSpeechToTextWebSocket(): WebSocket {
    const url = this.common.constructWsPath(`${this.baseEndpoint}/speech-to-text`);
    return new WebSocket(url);
  }

  async getProviders(
    configurationId: string,
    providerUserIds: string[]
  ): Promise<UserWithProfileAndPreference[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/providers`, {
      configurationId,
      providerUserIds,
    });
    return await this.common.getJSON(url);
  }

  async getSearchProviderFilters(configurationId: string): Promise<ProviderFilters> {
    const url = this.common.constructPath(`${this.baseEndpoint}/providers/search/filters`, {
      configurationId,
    });
    return await this.common.getJSON(url);
  }

  async me(): Promise<BasePatientWithEhrInformationId> {
    const url = this.common.constructPath(`${this.baseEndpoint}/patients/me`);
    const { patient } = await this.common.getJSON(url);
    return patient;
  }

  async createLog(args: AssistantReqAuditParams): Promise<boolean> {
    const url = this.common.constructPath(`${this.baseEndpoint}/logs`);
    const { success } = await this.common.postJSON(url, args, 'POST');
    return success;
  }

  async getDashboard(assistantId: string): Promise<{
    previsitFormSets: PrevisitFormSetWithDetails[];
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${assistantId}/dashboard`);
    return await this.common.getJSON(url);
  }
}
